// src/features/test-plans/api/useTestPlans.ts
// Combined hooks for Test Plans, Builds, Platforms, and Test Case Assignments
// All endpoints verified against openapi.json (testplan-service)
// All paths via API_CONFIG.ENDPOINTS

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { API_CONFIG } from '@/lib/config/api.config'
import type {
    TestPlan,
    TestPlansResponse,
    CreateTestPlanRequest,
    UpdateTestPlanRequest,
    Build,
    BuildsResponse,
    CreateBuildRequest,
    UpdateBuildRequest,
    Platform,
    PlatformsResponse,
    CreatePlatformRequest,
    UpdatePlatformRequest,
    AssignTestCaseRequest,
    AssignedTestCasesResponse,
} from '@/types/testplan.types'

// ============================================================================
// Query Keys
// ============================================================================

export const testPlanKeys = {
    all: ['testplans'] as const,
    lists: () => [...testPlanKeys.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
        [...testPlanKeys.lists(), filters] as const,
    details: () => [...testPlanKeys.all, 'detail'] as const,
    detail: (id: number) => [...testPlanKeys.details(), id] as const,
    testcases: (id: number) => [...testPlanKeys.all, 'testcases', id] as const,
}

export const buildKeys = {
    all: ['builds'] as const,
    lists: () => [...buildKeys.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
        [...buildKeys.lists(), filters] as const,
    details: () => [...buildKeys.all, 'detail'] as const,
    detail: (id: number) => [...buildKeys.details(), id] as const,
}

export const platformKeys = {
    all: ['platforms'] as const,
    lists: () => [...platformKeys.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
        [...platformKeys.lists(), filters] as const,
    details: () => [...platformKeys.all, 'detail'] as const,
    detail: (id: number) => [...platformKeys.details(), id] as const,
}

// ============================================================================
// Test Plans — CRUD
// OpenAPI: POST/GET /api/testplans, GET/PUT/DELETE /api/testplans/{testplan_id}
// ============================================================================

// List test plans with optional filters
// GET /api/testplans — params: project_id?, active? (default true), is_open?, limit (default 100), offset (default 0)
export const useTestPlans = (params?: {
    project_id?: number
    active?: boolean
    is_open?: boolean
    limit?: number
    offset?: number
}) => {
    return useQuery({
        queryKey: testPlanKeys.list(params),
        queryFn: async () => {
            const qp = new URLSearchParams()
            if (params?.project_id)
                qp.append('project_id', params.project_id.toString())
            if (params?.active !== undefined)
                qp.append('active', params.active.toString())
            if (params?.is_open !== undefined)
                qp.append('is_open', params.is_open.toString())
            if (params?.limit) qp.append('limit', params.limit.toString())
            if (params?.offset) qp.append('offset', params.offset.toString())

            const url = qp.toString()
                ? `${API_CONFIG.ENDPOINTS.TEST_PLANS.LIST}?${qp.toString()}`
                : API_CONFIG.ENDPOINTS.TEST_PLANS.LIST
            const { data } = await apiClient.get<TestPlansResponse>(url)
            return data.testplans
        },
    })
}

// Get single test plan by ID
// GET /api/testplans/{testplan_id}
export const useTestPlan = (id: number) => {
    return useQuery({
        queryKey: testPlanKeys.detail(id),
        queryFn: async () => {
            const { data } = await apiClient.get<TestPlan>(
                API_CONFIG.ENDPOINTS.TEST_PLANS.GET(id.toString())
            )
            return data
        },
        enabled: !!id,
    })
}

// Create test plan
// POST /api/testplans — body: { name (required), test_project_id (required), notes?, is_public? (default false), generate_api_key? (default false) }
// Returns 201
export const useCreateTestPlan = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (testplanData: CreateTestPlanRequest) => {
            const { data } = await apiClient.post<TestPlan>(
                API_CONFIG.ENDPOINTS.TEST_PLANS.CREATE,
                testplanData
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: testPlanKeys.lists() })
        },
    })
}

// Update test plan
// PUT /api/testplans/{testplan_id} — body: { name?, notes?, active?, is_open?, is_public? }
export const useUpdateTestPlan = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({
                               id,
                               data,
                           }: {
            id: number
            data: UpdateTestPlanRequest
        }) => {
            const response = await apiClient.put<TestPlan>(
                API_CONFIG.ENDPOINTS.TEST_PLANS.UPDATE(id.toString()),
                data
            )
            return response.data
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: testPlanKeys.lists() })
            queryClient.invalidateQueries({ queryKey: testPlanKeys.detail(data.id) })
        },
    })
}

// Delete test plan (soft delete)
// DELETE /api/testplans/{testplan_id} — Returns 204
export const useDeleteTestPlan = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (id: number) => {
            await apiClient.delete(
                API_CONFIG.ENDPOINTS.TEST_PLANS.DELETE(id.toString())
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: testPlanKeys.lists() })
        },
    })
}

// ============================================================================
// Test Plan Test Cases — Assignments
// OpenAPI: POST/GET /api/testplans/{testplan_id}/testcases
//          GET/PUT/DELETE /api/testplans/{testplan_id}/testcases/{assignment_id}
// ============================================================================

// List test cases assigned to a test plan
// GET /api/testplans/{testplan_id}/testcases — params: limit (default 100), offset (default 0)
export const useTestPlanTestCases = (testplanId: number) => {
    return useQuery({
        queryKey: testPlanKeys.testcases(testplanId),
        queryFn: async () => {
            const { data } = await apiClient.get<AssignedTestCasesResponse>(
                API_CONFIG.ENDPOINTS.TEST_PLANS.TEST_CASES(testplanId.toString())
            )
            return data.assignments
        },
        enabled: !!testplanId,
    })
}

// Assign test case to test plan
// POST /api/testplans/{testplan_id}/testcases — body: { tcversion_id (required), platform_id?, urgency?, node_order? }
// Returns 201
export const useAssignTestCase = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({
                               testplanId,
                               data,
                           }: {
            testplanId: number
            data: AssignTestCaseRequest
        }) => {
            const response = await apiClient.post(
                API_CONFIG.ENDPOINTS.TEST_PLANS.ASSIGN_TESTCASE(testplanId.toString()),
                data
            )
            return response.data
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: testPlanKeys.testcases(variables.testplanId),
            })
            queryClient.invalidateQueries({
                queryKey: testPlanKeys.detail(variables.testplanId),
            })
        },
    })
}

// Remove test case from test plan
// DELETE /api/testplans/{testplan_id}/testcases/{assignment_id} — Returns 204
export const useRemoveTestCase = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({
                               testplanId,
                               assignmentId,
                           }: {
            testplanId: number
            assignmentId: number
        }) => {
            await apiClient.delete(
                API_CONFIG.ENDPOINTS.TEST_PLANS.REMOVE_TESTCASE(
                    testplanId.toString(),
                    assignmentId.toString()
                )
            )
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: testPlanKeys.testcases(variables.testplanId),
            })
            queryClient.invalidateQueries({
                queryKey: testPlanKeys.detail(variables.testplanId),
            })
        },
    })
}

// ============================================================================
// Builds — CRUD
// OpenAPI: POST/GET /api/builds, GET/PUT/DELETE /api/builds/{build_id}
// ============================================================================

// List builds with optional filters
// GET /api/builds — params: testplan_id?, active? (default true), is_open?, limit (default 100), offset (default 0)
export const useBuilds = (params?: {
    testplan_id?: number
    active?: boolean
    is_open?: boolean
    limit?: number
    offset?: number
}) => {
    return useQuery({
        queryKey: buildKeys.list(params),
        queryFn: async () => {
            const qp = new URLSearchParams()
            if (params?.testplan_id)
                qp.append('testplan_id', params.testplan_id.toString())
            if (params?.active !== undefined)
                qp.append('active', params.active.toString())
            if (params?.is_open !== undefined)
                qp.append('is_open', params.is_open.toString())
            if (params?.limit) qp.append('limit', params.limit.toString())
            if (params?.offset) qp.append('offset', params.offset.toString())

            const url = qp.toString()
                ? `${API_CONFIG.ENDPOINTS.BUILDS.LIST}?${qp.toString()}`
                : API_CONFIG.ENDPOINTS.BUILDS.LIST
            const { data } = await apiClient.get<BuildsResponse>(url)
            return data.builds
        },
    })
}

// Get single build by ID (includes execution statistics)
// GET /api/builds/{build_id}
export const useBuild = (id: number) => {
    return useQuery({
        queryKey: buildKeys.detail(id),
        queryFn: async () => {
            const { data } = await apiClient.get<Build>(
                API_CONFIG.ENDPOINTS.BUILDS.GET(id.toString())
            )
            return data
        },
        enabled: !!id,
    })
}

// Create build
// POST /api/builds — body: { testplan_id (required), name (required), notes?, release_date?, is_open? (default true) }
// Returns 201
export const useCreateBuild = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (buildData: CreateBuildRequest) => {
            const { data } = await apiClient.post<Build>(
                API_CONFIG.ENDPOINTS.BUILDS.CREATE,
                buildData
            )
            return data
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: buildKeys.lists() })
            queryClient.invalidateQueries({
                queryKey: testPlanKeys.detail(data.testplan_id),
            })
        },
    })
}

// Update build
// PUT /api/builds/{build_id} — body: { name?, notes?, active?, is_open?, release_date?, closed_on_date? }
export const useUpdateBuild = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({
                               id,
                               data,
                           }: {
            id: number
            data: UpdateBuildRequest
        }) => {
            const response = await apiClient.put<Build>(
                API_CONFIG.ENDPOINTS.BUILDS.UPDATE(id.toString()),
                data
            )
            return response.data
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: buildKeys.lists() })
            queryClient.invalidateQueries({ queryKey: buildKeys.detail(data.id) })
            queryClient.invalidateQueries({
                queryKey: testPlanKeys.detail(data.testplan_id),
            })
        },
    })
}

// Delete build (soft delete)
// DELETE /api/builds/{build_id} — Returns 204
export const useDeleteBuild = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({
                               id,
                               testplanId,
                           }: {
            id: number
            testplanId: number
        }) => {
            await apiClient.delete(API_CONFIG.ENDPOINTS.BUILDS.DELETE(id.toString()))
            return { testplanId }
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: buildKeys.lists() })
            queryClient.invalidateQueries({
                queryKey: testPlanKeys.detail(data.testplanId),
            })
        },
    })
}

// ============================================================================
// Platforms — CRUD
// OpenAPI: POST/GET /api/platforms, GET/PUT/DELETE /api/platforms/{platform_id}
// ============================================================================

// List platforms with optional filters
// GET /api/platforms — params: project_id?, is_open? (default true), limit (default 100), offset (default 0)
export const usePlatforms = (params?: {
    project_id?: number
    is_open?: boolean
    limit?: number
    offset?: number
}) => {
    return useQuery({
        queryKey: platformKeys.list(params),
        queryFn: async () => {
            const qp = new URLSearchParams()
            if (params?.project_id)
                qp.append('project_id', params.project_id.toString())
            if (params?.is_open !== undefined)
                qp.append('is_open', params.is_open.toString())
            if (params?.limit) qp.append('limit', params.limit.toString())
            if (params?.offset) qp.append('offset', params.offset.toString())

            const url = qp.toString()
                ? `${API_CONFIG.ENDPOINTS.PLATFORMS.LIST}?${qp.toString()}`
                : API_CONFIG.ENDPOINTS.PLATFORMS.LIST
            const { data } = await apiClient.get<PlatformsResponse>(url)
            return data.platforms
        },
    })
}

// Get single platform by ID
// GET /api/platforms/{platform_id}
export const usePlatform = (id: number) => {
    return useQuery({
        queryKey: platformKeys.detail(id),
        queryFn: async () => {
            const { data } = await apiClient.get<Platform>(
                API_CONFIG.ENDPOINTS.PLATFORMS.GET(id.toString())
            )
            return data
        },
        enabled: !!id,
    })
}

// Create platform
// POST /api/platforms — body: { test_project_id (required), name (required), notes?, enable_on_design? (default false), enable_on_execution? (default true) }
// Returns 201
export const useCreatePlatform = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (platformData: CreatePlatformRequest) => {
            const { data } = await apiClient.post<Platform>(
                API_CONFIG.ENDPOINTS.PLATFORMS.CREATE,
                platformData
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: platformKeys.lists() })
        },
    })
}

// Update platform
// PUT /api/platforms/{platform_id} — body: { name?, notes?, active?, is_open?, is_public? }
export const useUpdatePlatform = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({
                               id,
                               data,
                           }: {
            id: number
            data: UpdatePlatformRequest
        }) => {
            const response = await apiClient.put<Platform>(
                API_CONFIG.ENDPOINTS.PLATFORMS.UPDATE(id.toString()),
                data
            )
            return response.data
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: platformKeys.lists() })
            queryClient.invalidateQueries({ queryKey: platformKeys.detail(data.id) })
        },
    })
}

// Delete platform (soft delete)
// DELETE /api/platforms/{platform_id} — Returns 204
export const useDeletePlatform = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (id: number) => {
            await apiClient.delete(
                API_CONFIG.ENDPOINTS.PLATFORMS.DELETE(id.toString())
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: platformKeys.lists() })
        },
    })
}